import { BarChart3, Clock3, FileText, Globe2, LogOut, MapPinned, Settings, Trophy, UserCog, Users } from "lucide-react";
import { lazy, StrictMode, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import type {
  ArticleRow,
  AdminUser,
  AuthConfig,
  AuthResponse,
  AuthorRow,
  DeviceTimeSlotsResponse,
  DimensionRow,
  HongKongDistrictResponse,
  MemberFilter,
  OverviewResponse,
  QueryParams,
  TimeSlotAnalysisResponse,
  TimeSlotDimension,
  TimeSlotSegmentLimit
} from "./api/client";
import { api } from "./api/client";
import { DateRangePicker } from "./components/DateRangePicker";
import { MemberFilter as MemberFilterControl } from "./components/MemberFilter";
import { ArticleDetailPage } from "./pages/ArticleDetailPage";
import { AccountManagementPage } from "./pages/AccountManagementPage";
import { AccountStatusPage, LoginPage } from "./pages/AuthPages";
import { ArticlesPage } from "./pages/ArticlesPage";
import { AuthorsPage } from "./pages/AuthorsPage";
import { DashboardPage } from "./pages/DashboardPage";
import { DeviceAnalysisPage } from "./pages/DeviceAnalysisPage";
import { PublicRankingsPage } from "./pages/PublicRankingsPage";
import { ReferrersPage } from "./pages/ReferrersPage";
import { SettingsPage } from "./pages/SettingsPage";
import { TimeSlotsPage } from "./pages/TimeSlotsPage";
import "./styles.css";
import { defaultReportingRange } from "./utils/reporting-date";
import { resolveTimeSlotSegmentLimit, type DeviceTimeSlotScope, type GeoTimeSlotScope } from "./utils/time-slots";

const GeoAnalysisPage = lazy(() =>
  import("./pages/GeoAnalysisPage").then((module) => ({ default: module.GeoAnalysisPage }))
);

type View =
  | "dashboard"
  | "articles"
  | "article-detail"
  | "authors"
  | "referrers"
  | "geo"
  | "devices"
  | "time-slots"
  | "public-rankings"
  | "accounts"
  | "settings";

const ARTICLE_FETCH_LIMIT = 200;

function App() {
  const [authConfig, setAuthConfig] = useState<AuthConfig>({ googleClientId: "", configured: false });
  const [auth, setAuth] = useState<AuthResponse>({ authenticated: false, user: null });
  const [authChecking, setAuthChecking] = useState(true);
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState("");
  const [view, setView] = useState<View>("dashboard");
  const [member, setMember] = useState<MemberFilter>("all");
  const [range, setRange] = useState(defaultReportingRange);
  const [search, setSearch] = useState("");
  const [selectedArticleId, setSelectedArticleId] = useState("");
  const [overview, setOverview] = useState<OverviewResponse | null>(null);
  const [articles, setArticles] = useState<ArticleRow[]>([]);
  const [authors, setAuthors] = useState<AuthorRow[]>([]);
  const [referrers, setReferrers] = useState<DimensionRow[]>([]);
  const [geo, setGeo] = useState<DimensionRow[]>([]);
  const [hongKongDistricts, setHongKongDistricts] = useState<HongKongDistrictResponse | null>(null);
  const [devices, setDevices] = useState<DimensionRow[]>([]);
  const [deviceTimeSlots, setDeviceTimeSlots] = useState<DeviceTimeSlotsResponse | null>(null);
  const [timeSlotDimension, setTimeSlotDimension] = useState<TimeSlotDimension>("device");
  const [deviceTimeSlotScope, setDeviceTimeSlotScope] = useState<DeviceTimeSlotScope>("all");
  const [geoTimeSlotScope, setGeoTimeSlotScope] = useState<GeoTimeSlotScope>("top10");
  const [timeSlotAnalysis, setTimeSlotAnalysis] = useState<TimeSlotAnalysisResponse | null>(null);
  const [articleDetail, setArticleDetail] = useState<{
    article: ArticleRow | null;
    daily: OverviewResponse["daily"];
    hourly: Array<{ view_hour: string; views: number; unique_visitors: number }>;
  } | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const loadSequence = useRef(0);

  const query = useMemo<QueryParams>(
    () => ({ from: range.from, to: range.to, member, q: search, limit: ARTICLE_FETCH_LIMIT }),
    [range.from, range.to, member, search]
  );
  const timeSlotSegmentLimit: TimeSlotSegmentLimit = resolveTimeSlotSegmentLimit(timeSlotDimension, geoTimeSlotScope);
  const usesAdminData = view !== "settings" && view !== "public-rankings" && view !== "accounts";
  const currentUser: AdminUser | null = auth.authenticated ? auth.user : null;
  const hasActiveAccess = currentUser?.status === "active";

  useEffect(() => {
    async function loadAuth() {
      try {
        const [config, session] = await Promise.all([api.authConfig(), api.me()]);
        setAuthConfig(config);
        setAuth(session);
      } catch (loadError) {
        setAuthError(loadError instanceof Error ? loadError.message : String(loadError));
      } finally {
        setAuthChecking(false);
      }
    }
    void loadAuth();
  }, []);

  useEffect(() => {
    if (!auth.authenticated || auth.user.status === "active") return;
    const timer = window.setInterval(() => {
      void api.me().then(setAuth).catch(() => undefined);
    }, 15_000);
    return () => window.clearInterval(timer);
  }, [auth]);

  async function load() {
    const sequence = ++loadSequence.current;
    setLoading(true);
    setError("");
    const tasks: Promise<void>[] = [];
    const errors: string[] = [];
    function request<T>(label: string, fetchData: () => Promise<T>, apply: (data: T) => void, clear: () => void) {
      clear();
      tasks.push(Promise.resolve().then(fetchData).then((data) => {
        if (sequence === loadSequence.current) apply(data);
      }).catch((reason: unknown) => {
        if (sequence !== loadSequence.current) return;
        errors.push(`${label}：${reason instanceof Error ? reason.message : String(reason)}`);
        setError(errors.join("；"));
      }));
    }
    if (view === "dashboard") request("總覽", () => api.overview(query), setOverview, () => setOverview(null));
    if (view === "dashboard" || view === "articles") request("文章", () => api.articles(query), data => setArticles(data.items), () => setArticles([]));
    if (view === "authors") request("作者", () => api.authors(query), data => setAuthors(data.items), () => setAuthors([]));
    if (view === "referrers") request("來源", () => api.referrers(query), data => setReferrers(data.items), () => setReferrers([]));
    if (view === "geo") {
      request("全球地區", () => api.geo(query), data => setGeo(data.items), () => setGeo([]));
      request("香港地區", () => api.hongKongDistricts(query), setHongKongDistricts, () => setHongKongDistricts(null));
    }
    if (view === "devices") {
      request("裝置", () => api.devices(query), data => setDevices(data.items), () => setDevices([]));
      request("裝置時段", () => api.deviceTimeSlots(query), setDeviceTimeSlots, () => setDeviceTimeSlots(null));
    }
    if (view === "time-slots") request("時段", () => api.timeSlots(query, timeSlotDimension, timeSlotSegmentLimit), setTimeSlotAnalysis, () => setTimeSlotAnalysis(null));
    if (view === "article-detail" && selectedArticleId) request("文章明細", () => api.article(selectedArticleId, query), setArticleDetail, () => setArticleDetail(null));
    await Promise.all(tasks);
    if (sequence === loadSequence.current) setLoading(false);
  }

  useEffect(() => {
    if (!usesAdminData || !hasActiveAccess) return;
    void load();
    return () => { loadSequence.current++; };
  }, [query, selectedArticleId, timeSlotDimension, timeSlotSegmentLimit, view, usesAdminData, hasActiveAccess]);

  async function handleGoogleCredential(credential: string) {
    setAuthLoading(true);
    setAuthError("");
    try {
      setAuth(await api.googleLogin(credential));
    } catch (loginError) {
      setAuthError(loginError instanceof Error ? loginError.message : String(loginError));
    } finally {
      setAuthLoading(false);
    }
  }

  async function handleLogout() {
    setAuthLoading(true);
    try {
      await api.logout();
      window.google?.accounts.id.disableAutoSelect();
      setAuth({ authenticated: false, user: null });
      setView("dashboard");
    } catch (logoutError) {
      setAuthError(logoutError instanceof Error ? logoutError.message : String(logoutError));
    } finally {
      setAuthLoading(false);
    }
  }

  if (authChecking) {
    return <main className="auth-shell"><div className="auth-loading">正在確認登入狀態</div></main>;
  }
  if (!auth.authenticated) {
    return <LoginPage config={authConfig} loading={authLoading} error={authError} onCredential={handleGoogleCredential} />;
  }
  if (!hasActiveAccess) {
    return <AccountStatusPage user={auth.user} onLogout={() => void handleLogout()} />;
  }

  const nav = [
    { view: "dashboard" as const, label: "總覽", icon: BarChart3 },
    { view: "articles" as const, label: "文章", icon: FileText },
    { view: "authors" as const, label: "作者", icon: Users },
    { view: "referrers" as const, label: "來源", icon: Globe2 },
    { view: "geo" as const, label: "地區", icon: MapPinned },
    { view: "devices" as const, label: "裝置", icon: BarChart3 },
    { view: "time-slots" as const, label: "時段", icon: Clock3 },
    { view: "public-rankings" as const, label: "公開排行", icon: Trophy },
    ...(auth.user.role === "admin" ? [{ view: "accounts" as const, label: "帳號管理", icon: UserCog }] : []),
    { view: "settings" as const, label: "設定", icon: Settings }
  ];

  const content = () => {
    if (view === "dashboard") {
      return (
        <DashboardPage
          query={query}
          overview={overview}
          articles={articles}
          articleSearch={search}
          loading={loading}
          onArticleSearch={setSearch}
          onReload={load}
        />
      );
    }
    if (view === "articles") {
      return <ArticlesPage rows={articles} search={search} onSearch={setSearch} />;
    }
    if (view === "article-detail") {
      return <ArticleDetailPage articleId={selectedArticleId} detail={articleDetail} />;
    }
    if (view === "authors") return <AuthorsPage rows={authors} />;
    if (view === "referrers") return <ReferrersPage title="來源分析" rows={referrers} />;
    if (view === "geo") {
      return (
        <Suspense fallback={<div className="panel empty-state">正在載入精確地圖...</div>}>
          <GeoAnalysisPage rows={geo} hongKongDistricts={hongKongDistricts} />
        </Suspense>
      );
    }
    if (view === "devices") return <DeviceAnalysisPage rows={devices} timeSlots={deviceTimeSlots} />;
    if (view === "time-slots") {
      return (
        <TimeSlotsPage
          data={timeSlotAnalysis}
          dimension={timeSlotDimension}
          deviceScope={deviceTimeSlotScope}
          geoScope={geoTimeSlotScope}
          onDimensionChange={setTimeSlotDimension}
          onDeviceScopeChange={setDeviceTimeSlotScope}
          onGeoScopeChange={setGeoTimeSlotScope}
        />
      );
    }
    if (view === "public-rankings") return <PublicRankingsPage />;
    if (view === "accounts") return <AccountManagementPage currentUser={auth.user} />;
    return <SettingsPage />;
  };

  const topbarSubtitle =
    view === "public-rankings"
      ? "Public rankings API"
      : view === "settings"
        ? "系統設定"
        : view === "accounts"
          ? "Google 帳號與存取權限"
          : `${range.from} 至 ${range.to}`;

  return (
    <main className="app-shell">
      <aside className="sidebar">
        <div className="brand">
          <strong>HKET Analytics</strong>
          <span>Article views</span>
        </div>
        <nav>
          {nav.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.view}
                type="button"
                className={view === item.view ? "active" : ""}
                onClick={() => setView(item.view)}
              >
                <Icon size={17} aria-hidden="true" />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>
        <div className="sidebar-account">
          <div className="sidebar-account-identity">
            {auth.user.pictureUrl ? <img src={auth.user.pictureUrl} alt="" referrerPolicy="no-referrer" /> : null}
            <div>
              <strong>{auth.user.displayName || auth.user.email}</strong>
              <span>{auth.user.role === "admin" ? "管理員" : "一般使用者"}</span>
            </div>
          </div>
          <button type="button" className="sidebar-logout" onClick={() => void handleLogout()} disabled={authLoading} title="登出">
            <LogOut size={16} aria-hidden="true" />
            <span>登出</span>
          </button>
        </div>
      </aside>
      <section className="workspace">
        <header className="topbar">
          <div>
            <h1>香港大紀元文章觀看統計</h1>
            <span>{topbarSubtitle}</span>
          </div>
          {usesAdminData ? (
            <div className="filters">
              <DateRangePicker from={range.from} to={range.to} onChange={setRange} />
              <MemberFilterControl value={member} onChange={setMember} />
            </div>
          ) : null}
        </header>
        {usesAdminData && error ? <div className="error-state">{error}</div> : null}
        {view === "articles" && articles[0] ? (
          <div className="article-shortcut">
            <button
              type="button"
              onClick={() => {
                setSelectedArticleId(articles[0].article_id);
                setView("article-detail");
              }}
            >
              查看目前最高觀看文章趨勢
            </button>
          </div>
        ) : null}
        {content()}
      </section>
    </main>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <App />
  </StrictMode>
);
